import { Card } from '@sravni/react-design-system/lib/Card';
import { Col, Row } from '@sravni/react-design-system/lib/Grid';
import { Heading, Text } from '@sravni/react-design-system/lib/Typography';
import {
  AgreementAdvertisingCheckbox,
  AgreementPersonalDataCheckbox,
  ServerErrorMessage,
} from '@sravni/react-silent-authentication-form';
import cn from 'classnames';
import React, { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { FIELD_NAME } from '../../../constants';
import { GiftBanner } from '../../GiftBanner';
import { ConsentsScreenTexts } from '../ConsentsScreen.texts';
import type { ContentProps } from '../types';

import styles from './ConsentsScreen.module.scss';
import { ConsentsSubmitButton } from './ConsentsSubmitButton';

export type ContentDesktopProps = ContentProps & {
  title: string;
  onReturnButtonClick: () => void;
};

export const ConsentsScreenContentDesktop = (props: ContentDesktopProps) => {
  const {
    title,
    checkboxLabel,
    onChangeAgreementPersonalData,
    onChangeAgreementAdvertising,
    onReturnButtonClick,
    submitAttempt,
    onConsentsScreenOpen,
    onConsentsSubmitClick,
  } = props;

  const { formState, watch, trigger } = useFormContext();
  const { errors } = formState || {};
  const [agreementPersonalData, agreementAdvertising] = watch([
    FIELD_NAME.AGREEMENT_PERSONAL_DATA,
    FIELD_NAME.AGREEMENT_ADVERTISING,
  ]);

  const personalDataError = errors[FIELD_NAME.AGREEMENT_PERSONAL_DATA];

  const onChangePersonalData: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      trigger(FIELD_NAME.AGREEMENT_PERSONAL_DATA);
      onChangeAgreementPersonalData?.(event);
    },
    [onChangeAgreementPersonalData, trigger],
  );

  useEffect(() => {
    onConsentsScreenOpen?.({
      isAgreementPersonalData: agreementPersonalData,
      isAgreementAdvertising: agreementAdvertising,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isShowAdvertisingError = submitAttempt > 0 && !agreementAdvertising;
  const isShowPersonalDataError = personalDataError?.message || !agreementPersonalData;

  return (
    <Row
      gutter={[32, 0]}
      className={styles.contentDesktop}
    >
      <Col span={{ mobile: 12, tablet: 6 }}>
        <Card
          color="dark"
          className={styles.contentCard}
        >
          <Heading
            level={4}
            className={styles.contentTitle}
          >
            {title}
          </Heading>

          <Card
            size={16}
            color="light"
            className={cn('h-mt-16', {
              [styles.advertisingCheckBoxError]: isShowAdvertisingError,
            })}
          >
            <AgreementAdvertisingCheckbox
              className={styles.agreementAdvertisingCheckbox}
              onChange={onChangeAgreementAdvertising}
            >
              {checkboxLabel}
            </AgreementAdvertisingCheckbox>
          </Card>

          <ServerErrorMessage className="h-mt-24" />

          <ConsentsSubmitButton
            onReturnButtonClick={onReturnButtonClick}
            onConsentsSubmitClick={onConsentsSubmitClick}
          />
        </Card>
      </Col>
      <Col span={{ mobile: 12, tablet: 6 }}>
        <GiftBanner className={styles.giftBanner}>
          <AgreementPersonalDataCheckbox
            className={styles.agreementCheckbox}
            onChange={onChangePersonalData}
            asListItem
          />
          <Text
            size={12}
            className={cn(styles.error, { [styles.hiddenError]: !isShowPersonalDataError })}
          >
            {personalDataError?.message || ConsentsScreenTexts.PersonalDataErrorMessage}
          </Text>
        </GiftBanner>
      </Col>
    </Row>
  );
};
